import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  Collapse,
  Dialog,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  LinearProgress,
  Stack,
  Tab,
  Tabs,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
  alpha,
  useTheme,
} from '@mui/material';
import {
  Close as CloseIcon,
  ArrowForward as ArrowForwardIcon,
  HelpOutline as HelpOutlineIcon,
  Launch as LaunchIcon,
  Lightbulb as LightbulbIcon,
  Lock as LockIcon,
} from '@mui/icons-material';
import MarkdownRenderer from '../common/MarkdownRenderer';
import PracticeEntry from '../practice/PracticeEntry';

// Fixed percentages a flag maps to, stored as-is in LessonProgress.confidence (0-100
// int) — kept off 0 deliberately so "0" stays reserved as the backend's "never rated"
// sentinel (see isLessonReady in TrackRoadmapPage.jsx and evidence_intelligence.py's
// same convention) no matter which flag a learner picks.
const CONFIDENCE_FLAGS = [
  { value: 25, label: 'Need help', emoji: '😟' },
  { value: 50, label: 'Partially understood', emoji: '😐' },
  { value: 75, label: 'Happy', emoji: '🙂' },
  { value: 100, label: 'Very happy', emoji: '😄' },
];

// ── Task guidance dialog ─────────────────────────────────────────────────────

function taskGuideMarkdown(task = {}, lesson = {}) {
  const safeTask = task || {};
  const explicitGuide = safeTask.guidance_markdown?.trim();
  // A task with authored, task-specific guidance is self-sufficient — stacking the
  // generic Input/Process/Output template underneath it just repeats the same
  // instructions in vaguer words. Only fall back to the template when nothing
  // task-specific was written.
  if (explicitGuide) return explicitGuide;

  const taskText = safeTask.text || 'Complete the task';
  const deliverable = lesson.deliverable || 'Saved evidence in the expected repo path.';
  const repoPath = lesson.vscode_path_hint || 'the suggested repo path for this lesson';

  return [
    '## Task guide',
    '',
    `- **Input:** The lesson scenario, resources, and any starter file or dataset for: ${taskText}`,
    `- **Process:** Do the smallest real action needed in your tool, notebook, query editor, dashboard, or repo.`,
    `- **Output:** Save the result, file, screenshot, query, note, or command output under \`${repoPath}\`.`,
    `- **Success:** The evidence supports the lesson deliverable: ${deliverable}`,
    '',
    'Keep it small. If the full work is already in VS Code or GitHub, reference the file path and avoid duplicating it here.',
  ].join('\n');
}

const markdownHeadingSlug = (value = '') => String(value)
  .toLowerCase()
  .replace(/[^a-z0-9\s-]/g, '')
  .trim()
  .replace(/\s+/g, '-');

const selectMarkdownSection = (markdown, hash) => {
  const wanted = String(hash || '').replace(/^#/, '');
  if (!wanted) return markdown;
  const lines = String(markdown || '').split('\n');
  let inFence = false;
  let start = -1;
  for (let index = 0; index < lines.length; index += 1) {
    const trimmed = lines[index].trim();
    if (trimmed.startsWith('```')) {
      inFence = !inFence;
      continue;
    }
    if (inFence) continue;
    const match = /^(#{1,6})\s+(.+)$/.exec(trimmed);
    if (match && markdownHeadingSlug(match[2]) === wanted) {
      start = index;
      break;
    }
  }
  if (start < 0) return markdown;
  const level = /^(#{1,6})\s+/.exec(lines[start].trim())[1].length;
  let end = lines.length;
  inFence = false;
  for (let index = start + 1; index < lines.length; index += 1) {
    const trimmed = lines[index].trim();
    if (trimmed.startsWith('```')) {
      inFence = !inFence;
      continue;
    }
    if (inFence) continue;
    const match = /^(#{1,6})\s+/.exec(trimmed);
    if (match && match[1].length <= level) {
      end = index;
      break;
    }
  }
  return lines.slice(start, end).join('\n');
};

const TaskGuidanceDialog = ({ task, lesson, open, onClose }) => {
  const [guide, setGuide] = useState(null);
  const [guideMarkdown, setGuideMarkdown] = useState('');
  const [guideError, setGuideError] = useState('');

  useEffect(() => {
    if (!guide?.href) return undefined;
    const [path, hash = ''] = guide.href.split('#');
    let cancelled = false;
    setGuideMarkdown('');
    setGuideError('');
    fetch(path, { cache: 'no-cache' })
      .then((response) => {
        if (!response.ok) throw new Error('not found');
        return response.text();
      })
      .then((markdown) => {
        if (!cancelled) setGuideMarkdown(selectMarkdownSection(markdown, hash));
      })
      .catch(() => {
        if (!cancelled) setGuideError('Could not load this guide. Please try again.');
      });
    return () => { cancelled = true; };
  }, [guide]);

  const closeGuide = () => {
    setGuide(null);
    setGuideMarkdown('');
    setGuideError('');
  };

  useEffect(() => {
    setGuide(null);
    setGuideMarkdown('');
    setGuideError('');
  }, [task, open]);

  const closeTask = () => {
    closeGuide();
    onClose();
  };

  return <>
  <Dialog
    open={open}
    onClose={closeTask}
    fullWidth
    maxWidth="sm"
    scroll="paper"
    PaperProps={{
      sx: {
        width: { xs: 'calc(100% - 20px)', sm: '100%' },
        m: { xs: 1.25, sm: 4 },
        maxHeight: { xs: 'calc(100% - 20px)', sm: 'calc(100% - 64px)' },
      },
    }}
  >
    <DialogTitle
      sx={{
        pr: 6,
        fontWeight: 800,
        fontSize: { xs: '0.9rem', sm: '1rem' },
        lineHeight: 1.25,
        overflowWrap: 'break-word',
      }}
    >
      {task?.text || 'Task guide'}
      <IconButton onClick={closeTask} size="small" sx={{ position: 'absolute', right: 8, top: 8 }}>
        <CloseIcon fontSize="small" />
      </IconButton>
    </DialogTitle>
    <DialogContent dividers sx={{ px: { xs: 1.5, sm: 3 }, overflowX: 'hidden' }}>
      <MarkdownRenderer content={taskGuideMarkdown(task, lesson)} onOpenMarkdownLink={setGuide} />
    </DialogContent>
  </Dialog>
  <Dialog open={Boolean(guide)} onClose={closeGuide} maxWidth="md" fullWidth>
    <DialogTitle sx={{ pr: 6, fontWeight: 800 }}>
      {guide?.label || 'Step-by-step guide'}
      <IconButton onClick={closeGuide} size="small" sx={{ position: 'absolute', right: 12, top: 10 }}>
        <CloseIcon />
      </IconButton>
    </DialogTitle>
    <DialogContent dividers sx={{ maxHeight: '78vh', overflowY: 'auto', px: { xs: 2, sm: 4 }, py: 3 }}>
      {!guideMarkdown && !guideError && <LinearProgress />}
      {guideError && <Alert severity="error">{guideError}</Alert>}
      {guideMarkdown && <MarkdownRenderer content={guideMarkdown} onOpenMarkdownLink={setGuide} />}
    </DialogContent>
  </Dialog>
  </>;
};

// ── Shared sub-components ────────────────────────────────────────────────────

export const StepHeader = ({ index, title }) => {
  const theme = useTheme();
  return (
    <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 1.5 }}>
      <Box sx={{
        width: 28, height: 28, borderRadius: '50%', display: 'flex',
        alignItems: 'center', justifyContent: 'center',
        bgcolor: alpha(theme.palette.primary.main, 0.15), color: 'primary.dark',
        fontWeight: 800, fontSize: 14, flexShrink: 0,
      }}>
        {index}
      </Box>
      <Typography variant="h6" sx={{ fontWeight: 800 }}>{title}</Typography>
    </Stack>
  );
};

const SQL_KEYWORD_DEFS = {
  SELECT:       'Picks which columns appear in your results.',
  FROM:         'Names the table (or tables) to read data from.',
  WHERE:        'Filters rows — only rows matching the condition are kept.',
  'ORDER BY':   'Sorts results. Add DESC to flip order (largest or latest first).',
  'GROUP BY':   'Groups rows with the same value so you can count or sum them.',
  HAVING:       'Filters groups after GROUP BY — like WHERE, but for groups.',
  JOIN:         'Combines two tables by matching rows on a shared column.',
  'LEFT JOIN':  'Returns every left-table row, even when there is no right-table match.',
  'INNER JOIN': 'Returns only rows that have a match in both tables.',
  COUNT:        'Counts how many rows are in a group.',
  SUM:          'Adds up values in a column.',
  AVG:          'Calculates the mean value of a column.',
  MAX:          'Returns the highest value in a column.',
  MIN:          'Returns the lowest value in a column.',
  DISTINCT:     'Removes duplicate rows from the result.',
  LIMIT:        'Caps the number of rows returned — useful for previewing.',
  AS:           'Renames a column or table in the output (alias).',
  CASE:         'Adds if/else logic inside a query.',
};

const learnerFlow = ['Lesson', 'Tasks', 'Daily evidence', 'Journal', 'Weekly proof'];

function lessonKind(lesson = {}) {
  const text = [
    lesson.learning_path_key,
    lesson.title,
    lesson.phase,
    lesson.location,
    lesson.deliverable,
    lesson.concept,
  ].join(' ').toLowerCase();

  if (/\bazure\b|fabric|databricks|adf|synapse|lakehouse|spark|delta|snowflake/.test(text)) return 'azure';
  if (/\bsql\b|query|database|join|table|schema|group by|window/.test(text)) return 'sql';
  if (/python|pandas|notebook|script|etl|api|pytest|fastapi/.test(text)) return 'python';
  if (/excel|power bi|dashboard|chart|workbook|analysis|analyst|visual/.test(text)) return 'data';
  return 'general';
}

function repoPathFromLesson(lesson = {}, kind = 'general') {
  if (lesson.vscode_path_hint) return lesson.vscode_path_hint;

  const slug = lesson.slug || '';
  const weekMatch = slug.match(/week-(\d{2})/);
  const sprintMatch = slug.match(/sprint-(\d{2})/);
  const unit = weekMatch ? `week-${weekMatch[1]}` : sprintMatch ? `sprint-${sprintMatch[1]}` : 'current-week';
  const titleSlug = (lesson.title || 'lesson')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '')
    .slice(0, 48) || 'lesson';

  const deliverable = lesson.deliverable || '';
  const explicitPath = deliverable.match(/`([^`]*(?:\/|\\)[^`]*)`/);
  if (explicitPath?.[1]) return explicitPath[1];

  const weekPath = deliverable.match(/\b((?:notes|projects|screenshots|src|sql|workbooks|evidence)\/week-\d{2}\/[^\s`.,)]+)/i);
  if (weekPath?.[1]) return weekPath[1];

  const defaults = {
    azure: `projects/${unit}/${titleSlug}/`,
    sql: `sql/${unit}/${titleSlug}.sql`,
    python: `src/${unit}/${titleSlug}.py`,
    data: `projects/${unit}/${titleSlug}/`,
    general: `projects/${unit}/${titleSlug}/`,
  };

  return defaults[kind];
}

function outputProofForLesson(lesson = {}) {
  const kind = lessonKind(lesson);
  const artifactByKind = {
    azure: 'Pipeline, notebook, config, architecture note, or cloud run proof',
    sql: 'SQL script, schema, query result, data model, or execution evidence',
    python: 'Python script, notebook, test, CLI command, API, or automation output',
    data: 'Workbook, notebook, dashboard, chart, report, or analysis README',
    general: 'A concrete file, note, result, or project output',
  };
  const evidenceByKind = {
    azure: ['Cloud run screenshot or log', 'Exported pipeline/notebook/config', 'README or architecture note'],
    sql: ['Committed .sql file', 'Result table or screenshot', 'Row-count or correctness check'],
    python: ['Committed code file', 'Terminal output or test result', 'Small proof snippet if useful'],
    data: ['Workbook/dashboard/report file', 'Screenshot of the final output', 'Check showing the result is correct'],
    general: ['Committed artefact', 'Result screenshot or output', 'Short explanation of what changed'],
  };
  const jobReadyByKind = {
    azure: [
      'The design explains source, storage, transform, serving layer, security, and monitoring.',
      'A rerun, failure, or validation scenario is documented.',
      'The README can defend the architecture trade-off in interview language.',
    ],
    sql: [
      'The query answers a business question and includes assumptions.',
      'Joins, filters, and aggregations are validated with row counts or spot checks.',
      'The result is explained in plain English, not just pasted as SQL.',
    ],
    python: [
      'The script is runnable from a clean environment with clear dependencies.',
      'Inputs, outputs, errors, and edge cases are handled or documented.',
      'There is proof of execution: terminal output, test result, or saved output file.',
    ],
    data: [
      'The analysis starts from a business question and ends with a decision-ready answer.',
      'The workbook, dashboard, or chart is clean enough for a non-technical reviewer.',
      'Data cleaning and validation choices are documented before conclusions are trusted.',
    ],
    general: [
      'The artefact solves the stated problem, not just demonstrates a tool.',
      'The result can be reviewed from the repo without asking the learner for context.',
      'The journal explains the main decision and the remaining limitation.',
    ],
  };

  return {
    artifact: artifactByKind[kind],
    repoPath: repoPathFromLesson(lesson, kind),
    evidence: evidenceByKind[kind],
    checks: [
      'The required tasks are complete.',
      'The artefact is saved in the repo, not only on the platform.',
      'There is visible proof that it ran, calculated, or rendered correctly.',
      'The journal entry explains the key decision and what to review next.',
    ],
    jobReadyChecks: jobReadyByKind[kind],
  };
}

function rubricForLesson(lesson = {}) {
  const kind = lessonKind(lesson);
  const byKind = {
    azure: {
      correctness: 'Pipeline/config/notebook matches the stated data flow and handles the expected source/target.',
      reproducibility: 'A reviewer can find the setup, run steps, screenshots, and rerun notes.',
    },
    sql: {
      correctness: 'Query returns the intended rows or metric, with joins and filters checked.',
      reproducibility: 'The SQL file can be rerun against the stated tables or sample data.',
    },
    python: {
      correctness: 'Code reads the expected input, performs the stated transformation, and produces the expected output.',
      reproducibility: 'Dependencies, run command, input path, and output path are clear.',
    },
    data: {
      correctness: 'The analysis answers the business question with checked data and suitable calculations.',
      reproducibility: 'The workbook, query, notebook, or dashboard can be reopened and reviewed.',
    },
    general: {
      correctness: 'The artefact directly solves the target prompt.',
      reproducibility: 'A reviewer can find and inspect the saved work without extra explanation.',
    },
  };
  const specific = byKind[kind] || byKind.general;
  return [
    ['Correctness', specific.correctness],
    ['Clarity', 'Names, labels, comments, and notes make the work easy to scan.'],
    ['Reproducibility', specific.reproducibility],
    ['Evidence', 'Repo path, screenshot/output, and journal note prove the work happened.'],
    ['Professional communication', 'The final note explains the decision, not only the tool used.'],
  ];
}

function seniorPromptsForLesson(lesson = {}) {
  const kind = lessonKind(lesson);
  const shared = [
    'What could break if the input data, user behaviour, or environment changes?',
    'What is the monitoring, security, rerun, scale, and cost story?',
    'What proof would show that the result is still correct tomorrow?',
    'What architecture decision would another engineer challenge?',
    'What would change if this moved to another tool, cloud, team, or larger dataset?',
    'If this failed in production, what is the first runbook step?',
    'How would you explain the result and limitation to a non-technical stakeholder?',
    'What trade-off would you defend in an interview?',
  ];
  const byKind = {
    azure: [
      'How would this pipeline be monitored, alerted, rerun, and cost-controlled?',
      'Which identity, secret, RBAC, or network boundary protects the data?',
      'What would change between dev, test, and production?',
    ],
    sql: [
      'What index, constraint, or model change would improve reliability or performance?',
      'How would you detect duplicate, missing, late, or inconsistent rows?',
      'What backup, restore, permission, or audit concern belongs with this work?',
    ],
    python: [
      'How would the script handle bad files, missing columns, retries, and logging?',
      'What test or assertion would catch the most expensive failure?',
      'How would the code be packaged, scheduled, or called by another system?',
    ],
    data: [
      'What assumption could make the analysis misleading?',
      'How would a stakeholder misunderstand the chart, metric, or recommendation?',
      'What data-quality check should happen before the insight is trusted?',
    ],
    general: [
      'What operational risk would appear if this became a real workflow?',
      'How would another person review, rerun, or maintain the work?',
      'What limitation should be documented before sharing the result?',
    ],
  };

  return [...(byKind[kind] || byKind.general), ...shared];
}

// Replaces the old 3 separately-bordered "Weekly review reference" /
// "How this work is reviewed" / "Senior review prompts" boxes — same content,
// one shared frame with an internal tab switcher instead of 3 stacked cards
// that all looked identical and made a beginner unsure which one mattered.
const ReviewAndProofPanel = ({ lesson }) => {
  const theme = useTheme();
  const proof = outputProofForLesson(lesson);
  const rubric = rubricForLesson(lesson);
  const prompts = seniorPromptsForLesson(lesson);
  const [open, setOpen] = useState(false);
  const [tab, setTab] = useState(0);

  return (
    <Box
      sx={{
        mt: 2,
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 2,
        bgcolor: 'background.paper',
        overflow: 'hidden',
      }}
    >
      <Box
        onClick={() => setOpen((v) => !v)}
        sx={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          p: 1.5, cursor: 'pointer', userSelect: 'none',
        }}
      >
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 900 }}>
            Review &amp; practice tools
          </Typography>
          <Typography variant="caption" color="text.secondary">
            All optional — for checking your evidence, seeing how it's reviewed, and rehearsing interview depth.
          </Typography>
        </Box>
        <Typography variant="caption" color="text.disabled" sx={{ fontSize: '0.68rem', flexShrink: 0, ml: 1 }}>
          {open ? '▲ hide' : '▼ show'}
        </Typography>
      </Box>
      <Collapse in={open} timeout="auto" unmountOnExit>
        <Tabs
          value={tab}
          onChange={(_, v) => setTab(v)}
          variant="scrollable"
          scrollButtons="auto"
          sx={{
            minHeight: 36,
            borderTop: 1, borderBottom: 1, borderColor: 'divider',
            '& .MuiTab-root': { minHeight: 36, py: 0.75, textTransform: 'none', fontSize: '0.78rem', fontWeight: 600 },
          }}
        >
          <Tab label="Review reference" />
          <Tab label="How it's graded" />
          <Tab label="Interview prompts" />
        </Tabs>
        <Box sx={{ p: 1.5 }}>
          {tab === 0 && (
            <Stack spacing={1.25}>
              <Box
                component="code"
                sx={{
                  display: 'block',
                  p: 0.75,
                  borderRadius: 1,
                  bgcolor: alpha(theme.palette.primary.main, 0.06),
                  color: 'text.secondary',
                  fontFamily: 'monospace',
                  fontSize: '0.72rem',
                  lineHeight: 1.45,
                  overflowWrap: 'anywhere',
                  wordBreak: 'break-word',
                }}
              >
                {proof.repoPath}
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 800, textTransform: 'uppercase' }}>
                  Expected artefact
                </Typography>
                <Typography variant="body2">{proof.artifact}</Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 800, textTransform: 'uppercase' }}>
                  Useful evidence
                </Typography>
                <Stack direction="row" spacing={0.75} sx={{ mt: 0.5, flexWrap: 'wrap', rowGap: 0.75 }}>
                  {proof.evidence.map((item) => (
                    <Chip key={item} size="small" label={item} variant="outlined" />
                  ))}
                </Stack>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 800, textTransform: 'uppercase' }}>
                  Review checks
                </Typography>
                <Stack component="ul" spacing={0.35} sx={{ pl: 2.5, mt: 0.5, mb: 0 }}>
                  {[...proof.checks, ...proof.jobReadyChecks].map((check) => (
                    <Typography component="li" variant="body2" key={check}>{check}</Typography>
                  ))}
                </Stack>
              </Box>
            </Stack>
          )}
          {tab === 1 && (
            <Stack spacing={0.85}>
              <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic', mb: 0.5 }}>
                Reference only — use it to improve the work, not as a separate submission.
              </Typography>
              {rubric.map(([label, description]) => (
                <Box key={label}>
                  <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 800, textTransform: 'uppercase' }}>
                    {label}
                  </Typography>
                  <Typography variant="body2">{description}</Typography>
                </Box>
              ))}
            </Stack>
          )}
          {tab === 2 && (
            <Stack spacing={1}>
              <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                Use these when you want production-level depth for an interview.
              </Typography>
              <Stack component="ul" spacing={0.5} sx={{ pl: 2.5, m: 0 }}>
                {prompts.map((prompt) => (
                  <Typography component="li" variant="body2" key={prompt}>{prompt}</Typography>
                ))}
              </Stack>
            </Stack>
          )}
        </Box>
      </Collapse>
    </Box>
  );
};

// Parses concept markdown into scenario, body, and special sections (hidden tips,
// interview questions, reflection). Body has ### sub-headings stripped so the
// narrative flows without fragmentation — learners read a story, not an outline.
function parseConceptSections(concept) {
  const lines = concept.split('\n');

  // Scenario: first line starting with "**Scenario:**"
  let scenarioLine = null;
  let scenarioLineIndex = -1;
  for (let i = 0; i < lines.length; i++) {
    const trimmed = lines[i].trim();
    if (trimmed.startsWith('**Scenario:**')) {
      scenarioLine = trimmed.replace(/^\*\*Scenario:\*\*\s*/, '').trim();
      scenarioLineIndex = i;
      break;
    }
  }

  // Locate special sections
  const hiddenTipsIdx = lines.findIndex((l) => l.trim() === '## Hidden Tips');
  const interviewIdx = lines.findIndex((l) => l.trim() === '## Interview Questions');
  const reflectionIdx = lines.findIndex((l) => l.trim() === '## Reflection Questions');

  const specialStarts = [hiddenTipsIdx, interviewIdx, reflectionIdx]
    .filter((i) => i !== -1)
    .sort((a, b) => a - b);

  const bodyEnd = specialStarts.length > 0 ? specialStarts[0] : lines.length;

  // Body = everything before first special section, excluding scenario line,
  // with ### sub-headings removed so content flows as narrative
  const body = lines
    .filter((_, i) => i !== scenarioLineIndex && i < bodyEnd)
    .filter((line) => !line.trim().startsWith('### '))
    .join('\n')
    .trim();

  const extractSection = (startIdx) => {
    if (startIdx === -1) return null;
    const nextStart = specialStarts.find((i) => i > startIdx);
    const end = nextStart !== undefined ? nextStart : lines.length;
    return lines.slice(startIdx + 1, end).join('\n').trim() || null;
  };

  return {
    scenarioLine,
    body,
    hiddenTipsContent: extractSection(hiddenTipsIdx),
    interviewContent: extractSection(interviewIdx),
    reflectionContent: extractSection(reflectionIdx),
  };
}

function fallbackScenarioForLesson(lesson = {}) {
  const title = lesson.title || 'this lesson';
  const prompt = lesson.learn_prompt || lesson.lesson_prompt || '';
  const deliverable = lesson.deliverable || '';
  if (prompt && deliverable) {
    return `Use ${title} to answer: ${prompt} The expected proof is: ${deliverable}`;
  }
  if (prompt) return `Use ${title} to answer: ${prompt}`;
  if (deliverable) return `Use ${title} to produce: ${deliverable}`;
  return `Use ${title} to build one small piece of evidence you can explain and review.`;
}

// Tracks built for learners with prior experience (senior/interview prep), not the
// zero-knowledge "learn a new concept" path — their Interview/Reflection questions are
// curated prep tied to the learner's own past decisions, not a redundant quiz layer on
// top of a concept just introduced. Beginner tracks (python, sql, data-analyst, etc.)
// keep those sections hidden per the workload-reduction pass.
const EXPERIENCED_LEARNER_PATH_KEYS = new Set(['azure-data-engineer']);

export const ConceptPanel = ({ concept, lesson }) => {
  const theme = useTheme();
  const [open, setOpen] = useState(true);
  const [activeTab, setActiveTab] = useState(0);
  const [reflectOpen, setReflectOpen] = useState(false);
  const conceptText = concept?.trim() || '';
  const showInterviewAndReflection = EXPERIENCED_LEARNER_PATH_KEYS.has(lesson?.learning_path_key);

  const { scenarioLine, body, hiddenTipsContent, interviewContent, reflectionContent } =
    parseConceptSections(conceptText);
  const displayScenario = scenarioLine || fallbackScenarioForLesson(lesson);

  // Only scan for SQL keywords on lessons that are actually about SQL — several of these
  // keywords (AS, FROM, SUM, COUNT, MAX, MIN, CASE) are also common English words, so
  // scanning every lesson's prose false-positives on non-SQL lessons (e.g. an Excel lesson
  // that happens to say "used as a named Table").
  const detectedKeywords = lessonKind(lesson) === 'sql'
    ? Object.keys(SQL_KEYWORD_DEFS).filter((kw) => {
        const escaped = kw.replace(/[.*+?^${}()|[\]\\]/g, '\\$&').replace(/\s+/g, '\\s+');
        return new RegExp(`\\b${escaped}\\b`, 'i').test(conceptText);
      }).slice(0, 12)
    : [];

  const tabs = [
    { label: 'Concept', content: body || 'Start from the scenario, complete the required tasks, then save a concise journal note with the evidence path and the key decision.' },
    ...(hiddenTipsContent ? [{ label: 'Pro Tips', content: hiddenTipsContent }] : []),
    ...(showInterviewAndReflection && interviewContent ? [{ label: 'Interview Q&A', content: interviewContent }] : []),
  ];

  const safeTab = Math.min(activeTab, tabs.length - 1);

  const neutralBorder = alpha(theme.palette.text.primary, 0.1);
  const subtleBg = alpha(theme.palette.text.primary, 0.025);

  const mdStyles = {
    '& h2': { fontSize: '1.05rem', fontWeight: 700, mt: 2.5, mb: 0.75, color: 'text.primary', borderBottom: '1px solid', borderColor: neutralBorder, pb: 0.5 },
    '& p': { fontSize: '0.95rem', mb: 1.25, lineHeight: 1.8, color: 'text.primary' },
    '& strong': { color: 'text.primary', fontWeight: 700 },
    '& ul, & ol': { pl: 2.5, mb: 1 },
    '& li': { fontSize: '0.95rem', lineHeight: 1.75, mb: 0.5 },
    '& table': { width: '100%', borderCollapse: 'collapse', fontSize: '0.88rem', my: 1.5 },
    '& th': { bgcolor: alpha(theme.palette.text.primary, 0.05), fontWeight: 700 },
    '& th, & td': { border: '1px solid', borderColor: neutralBorder, px: 1.25, py: 0.75, textAlign: 'left' },
    '& code': { fontSize: '0.85rem', bgcolor: alpha(theme.palette.text.primary, 0.06), color: 'text.primary', px: 0.6, py: 0.15, borderRadius: 0.5, fontFamily: 'monospace' },
    '& pre': { bgcolor: alpha(theme.palette.text.primary, 0.05), border: '1px solid', borderColor: neutralBorder, borderRadius: 1, p: 1.75, overflowX: 'auto', my: 1.25 },
    '& pre code': { bgcolor: 'transparent', p: 0 },
    '& blockquote': { borderLeft: '3px solid', borderColor: neutralBorder, pl: 1.5, ml: 0, color: 'text.secondary', fontStyle: 'italic' },
    '& hr': { border: 0, borderTop: '1px solid', borderColor: neutralBorder, my: 2 },
  };

  return (
    <Box sx={{ borderRadius: 2, border: 1, borderColor: neutralBorder, overflow: 'hidden' }}>

      {/* Header — click to collapse */}
      <Box
        onClick={() => setOpen((v) => !v)}
        sx={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          px: 2, py: 1.25,
          bgcolor: alpha(theme.palette.text.primary, 0.04),
          borderBottom: open ? 1 : 0,
          borderColor: neutralBorder,
          cursor: 'pointer', userSelect: 'none',
        }}
      >
        <Stack direction="row" alignItems="center" spacing={1}>
          <LightbulbIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
          <Typography variant="caption" fontWeight={700} color="text.secondary"
            sx={{ letterSpacing: 1, textTransform: 'uppercase', fontSize: '0.68rem' }}>
            Concept Guide
          </Typography>
        </Stack>
        <Typography variant="caption" color="text.disabled" sx={{ fontSize: '0.68rem', fontWeight: 500 }}>
          {open ? '▲ collapse' : '▼ expand'}
        </Typography>
      </Box>

      {open && (
        <>
          {/* Scenario anchor */}
          {displayScenario && (
            <Box sx={{
              px: 2, py: 1.5,
              bgcolor: subtleBg,
              borderBottom: 1, borderColor: neutralBorder,
              borderLeft: '3px solid', borderLeftColor: 'text.secondary',
            }}>
              <Typography variant="caption" fontWeight={700} color="text.secondary"
                sx={{ display: 'block', mb: 0.5, textTransform: 'uppercase', letterSpacing: 1, fontSize: '0.62rem' }}>
                Today's scenario
              </Typography>
              <Typography variant="body2" sx={{ fontSize: '0.95rem', lineHeight: 1.75, color: 'text.primary', fontStyle: 'italic' }}>
                {displayScenario}
              </Typography>
            </Box>
          )}

          {/* SQL keyword quick-reference */}
          {detectedKeywords.length >= 2 && (
            <Box sx={{ px: 2, py: 1.25, bgcolor: subtleBg, borderBottom: 1, borderColor: neutralBorder }}>
              <Typography variant="caption" fontWeight={600} color="text.disabled"
                sx={{ display: 'block', mb: 0.75, textTransform: 'uppercase', letterSpacing: 1, fontSize: '0.62rem' }}>
                SQL keywords — hover for a definition
              </Typography>
              <Stack direction="row" flexWrap="wrap" gap={0.6}>
                {detectedKeywords.map((kw) => (
                  <Tooltip key={kw} title={SQL_KEYWORD_DEFS[kw]} arrow placement="top">
                    <Chip label={kw} size="small" sx={{
                      fontFamily: 'monospace', fontWeight: 600, fontSize: '0.7rem',
                      bgcolor: alpha(theme.palette.text.primary, 0.06),
                      color: 'text.secondary',
                      border: '1px solid', borderColor: neutralBorder,
                      cursor: 'help',
                      '&:hover': { bgcolor: alpha(theme.palette.text.primary, 0.1), color: 'text.primary' },
                    }} />
                  </Tooltip>
                ))}
              </Stack>
            </Box>
          )}

          {/* Tab bar — only shown when there are multiple tabs */}
          {tabs.length > 1 && (
            <Tabs
              value={safeTab}
              onChange={(_, v) => setActiveTab(v)}
              variant="scrollable"
              scrollButtons="auto"
              TabIndicatorProps={{ style: { height: 2, backgroundColor: theme.palette.text.primary } }}
              sx={{
                minHeight: 36,
                borderBottom: 1,
                borderColor: neutralBorder,
                bgcolor: alpha(theme.palette.text.primary, 0.02),
                '& .MuiTab-root': {
                  minHeight: 36, py: 0.75, px: 2,
                  textTransform: 'none', fontSize: '0.72rem', fontWeight: 500,
                  color: 'text.disabled',
                  '&.Mui-selected': { color: 'text.primary', fontWeight: 700 },
                },
              }}
            >
              {tabs.map((tab, i) => (
                <Tab key={tab.label} label={tab.label} value={i} />
              ))}
            </Tabs>
          )}

          {/* Tab content */}
          <Box sx={{ p: 2, ...mdStyles }}>
            <MarkdownRenderer content={tabs[safeTab]?.content || ''} />
          </Box>

          {/* Reflection Questions — collapsible footer, experienced-learner tracks only */}
          {showInterviewAndReflection && reflectionContent && (
            <Box sx={{ borderTop: 1, borderColor: neutralBorder }}>
              <Box
                onClick={() => setReflectOpen((v) => !v)}
                sx={{
                  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                  px: 2, py: 1,
                  bgcolor: subtleBg,
                  cursor: 'pointer', userSelect: 'none',
                }}
              >
                <Typography variant="caption" fontWeight={700} color="text.secondary"
                  sx={{ textTransform: 'uppercase', letterSpacing: 1, fontSize: '0.62rem' }}>
                  Reflection Questions
                </Typography>
                <Typography variant="caption" color="text.disabled" sx={{ fontSize: '0.68rem' }}>
                  {reflectOpen ? '▲ hide' : '▼ show'}
                </Typography>
              </Box>
              {reflectOpen && (
                <Box sx={{
                  px: 2, pb: 2, pt: 1,
                  bgcolor: subtleBg,
                  borderTop: 1, borderColor: neutralBorder,
                  '& p': { fontSize: '0.95rem', mb: 1, lineHeight: 1.75 },
                  '& strong': { color: 'text.primary' },
                  '& ol, & ul': { pl: 2.5 },
                  '& li': { fontSize: '0.95rem', lineHeight: 1.75, mb: 0.5 },
                }}>
                  <MarkdownRenderer content={reflectionContent} />
                </Box>
              )}
            </Box>
          )}
        </>
      )}
    </Box>
  );
};

// ── Main component ───────────────────────────────────────────────────────────
//
// Props:
//   lesson           — lessonDetail object from API
//   chips            — optional ReactNode[] — status chips rendered above the title
//   completedIndices — int[] — which task indices are checked
//   onToggleTask     — (index: number) => void
//   taskProgress     — 0-100 number for the task progress bar
//   practiceItem     — optional config object passed to PracticeEntry
//   notesContent     — optional ReactNode — content placed inside the Notes section
//   onNextLesson     — () => void — navigate to the next lesson (null if none)
//   displayTitle      — optional string — learner-facing title without changing stored lesson data
//   nextLessonTitle  — string — title of the next lesson (for the button label)
//   confidence       — 0-100 number — learner's self-rated confidence for this lesson
//   onConfidenceChange — (value: number) => void — persists the rating (required before the
//                        next lesson unlocks, when a journal step is present)

const LessonView = ({
  lesson,
  chips,
  completedIndices,
  onToggleTask,
  taskProgress,
  practiceItem,
  notesContent,
  onPracticeComplete,
  onNextLesson,
  displayTitle,
  nextLessonTitle,
  confidence,
  onConfidenceChange,
}) => {
  const theme = useTheme();
  const [guidanceTask, setGuidanceTask] = useState(null);
  const [resourceGuide, setResourceGuide] = useState(null);
  const [resourceMarkdown, setResourceMarkdown] = useState('');
  const [resourceGuideError, setResourceGuideError] = useState('');
  const [practiceComplete, setPracticeComplete] = useState(false);
  // Step 0 (concept) open by default; all others start collapsed
  const [openSteps, setOpenSteps] = useState({ 0: true });
  const toggleStep = (i) => setOpenSteps((prev) => ({ ...prev, [i]: !prev[i] }));

  const handlePracticeComplete = useCallback((...args) => {
    setPracticeComplete(true);
    onPracticeComplete?.(...args);
  }, [onPracticeComplete]);

  useEffect(() => {
    if (!resourceGuide?.href) return undefined;
    const [path, hash = ''] = resourceGuide.href.split('#');
    let cancelled = false;
    setResourceMarkdown('');
    setResourceGuideError('');
    fetch(path, { cache: 'no-cache' })
      .then((response) => {
        if (!response.ok) throw new Error('not found');
        return response.text();
      })
      .then((markdown) => {
        if (!cancelled) setResourceMarkdown(selectMarkdownSection(markdown, hash));
      })
      .catch(() => {
        if (!cancelled) setResourceGuideError('Could not load this guide. Please try again.');
      });
    return () => { cancelled = true; };
  }, [resourceGuide]);

  const closeResourceGuide = () => {
    setResourceGuide(null);
    setResourceMarkdown('');
    setResourceGuideError('');
  };

  const hasPracticeStep = Boolean(practiceItem);
  const allTasksDone = lesson.tasks.length === 0 || completedIndices.length >= lesson.tasks.length;
  // confidence=0 is the unset default (see backend LessonProgress.confidence), so a
  // truthy value is the only signal that the learner has actually rated themselves —
  // required as the last step of the journal before the next lesson unlocks, same as
  // the task checklist. Only enforced when the parent actually wires up rating.
  const confidenceRated = Boolean(confidence);
  const requiresConfidence = Boolean(onConfidenceChange);
  const lessonReady = allTasksDone && (!requiresConfidence || confidenceRated);

  const stepDescriptions = {
    concept: 'Read the scenario and core idea.',
    resources: 'Open only what helps you complete today’s work.',
    tasks: 'Do the real work in your tool or repo.',
    journal: 'Summarize proof, decisions, and what to review.',
    practice: 'Optional rehearsal when the idea still needs to click.',
  };

  // Build ordered steps — each step is a { label, content } the learner works through in sequence
  const steps = [
    {
      key: 'concept',
      label: 'Understand the concept',
      content: <ConceptPanel concept={lesson.concept} lesson={lesson} />,
    },
    ...(lesson.resources?.length > 0 ? [{
      key: 'resources',
      label: 'Explore these resources',
      content: (
        <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', rowGap: 1 }}>
          {lesson.resources.map((resource) => {
            const internalMarkdown = resource.href?.startsWith('/')
              && resource.href.split('#')[0].endsWith('.md');
            return (
              <Button
                key={`${resource.href}-${resource.label}`}
                href={internalMarkdown ? undefined : resource.href}
                target={internalMarkdown ? undefined : '_blank'}
                rel={internalMarkdown ? undefined : 'noreferrer'}
                onClick={internalMarkdown ? () => setResourceGuide(resource) : undefined}
                size="small"
                variant="outlined"
                endIcon={internalMarkdown ? <HelpOutlineIcon /> : <LaunchIcon />}
              >
                {resource.label}
              </Button>
            );
          })}
        </Stack>
      ),
    }] : []),
    {
      key: 'tasks',
      label: "Complete today's tasks",
      content: (
        <>
          <Stack spacing={0.5}>
            {lesson.tasks.map((task, index) => (
              <Stack key={index} direction="row" alignItems="flex-start" spacing={0.5}>
                <FormControlLabel
                  control={<Checkbox checked={completedIndices.includes(index)} onChange={() => onToggleTask(index)} />}
                  label={task.text}
                  sx={{
                    alignItems: 'flex-start', m: 0, flex: 1,
                    '& .MuiFormControlLabel-label': {
                      pt: 1,
                      color: completedIndices.includes(index) ? 'text.secondary' : 'text.primary',
                      textDecoration: completedIndices.includes(index) ? 'line-through' : 'none',
                    },
                  }}
                />
                <Tooltip title="Task guide">
                  <IconButton size="small" onClick={() => setGuidanceTask(task)}
                    sx={{ mt: 0.75, color: 'primary.main', flexShrink: 0 }}>
                    <HelpOutlineIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Stack>
            ))}
          </Stack>
          <Box sx={{ mt: 1.5 }}>
            <LinearProgress color="success" variant="determinate" value={taskProgress} sx={{ height: 6, borderRadius: 2 }} />
            <Typography variant="caption" color="text.secondary">
              {completedIndices.length} of {lesson.tasks.length} tasks done
            </Typography>
          </Box>
        </>
      ),
    },
    ...(notesContent || requiresConfidence ? [{
      key: 'journal',
      label: 'Add to your learning journal',
      content: (
        <>
          {notesContent}
          {requiresConfidence && (
            <Box sx={{ mt: notesContent ? 2.5 : 0 }}>
              <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 0.5 }}>
                Rate your confidence
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                How confident are you that you could redo this lesson's work unassisted? Required before the next lesson unlocks.
              </Typography>
              <ToggleButtonGroup
                exclusive
                value={confidence || null}
                onChange={(_, next) => { if (next !== null) onConfidenceChange(next); }}
                size="small"
                sx={{ flexWrap: 'wrap' }}
              >
                {CONFIDENCE_FLAGS.map((flag) => (
                  <ToggleButton key={flag.value} value={flag.value} sx={{ textTransform: 'none', px: 1.5 }}>
                    {flag.emoji}&nbsp;{flag.label}
                  </ToggleButton>
                ))}
              </ToggleButtonGroup>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                {confidenceRated ? ' ' : 'Not yet rated'}
              </Typography>
            </Box>
          )}
        </>
      ),
    }] : []),
    ...(practiceItem ? [{
      key: 'practice',
      label: 'Optional practice',
      content: <PracticeEntry item={practiceItem} onComplete={handlePracticeComplete} />,
    }] : []),
  ];

  const hasDeliverable = Boolean(lesson.deliverable);

  return (
    <Box>
      <TaskGuidanceDialog
        task={guidanceTask}
        lesson={lesson}
        open={Boolean(guidanceTask)}
        onClose={() => setGuidanceTask(null)}
      />
      <Dialog open={Boolean(resourceGuide)} onClose={closeResourceGuide} maxWidth="md" fullWidth>
        <DialogTitle sx={{ pr: 6, fontWeight: 800 }}>
          {resourceGuide?.label || 'Step-by-step guide'}
          <IconButton onClick={closeResourceGuide} size="small" sx={{ position: 'absolute', right: 12, top: 10 }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers sx={{ maxHeight: '78vh', overflowY: 'auto', px: { xs: 2, sm: 4 }, py: 3 }}>
          {!resourceMarkdown && !resourceGuideError && <LinearProgress />}
          {resourceGuideError && <Alert severity="error">{resourceGuideError}</Alert>}
          {resourceMarkdown && (
            <MarkdownRenderer content={resourceMarkdown} onOpenMarkdownLink={setResourceGuide} />
          )}
        </DialogContent>
      </Dialog>

      {/* Lesson title */}
      <Box sx={{ mb: 4 }}>
        {chips?.length > 0 && (
          <Stack direction="row" spacing={1} sx={{ mb: 1, flexWrap: 'wrap', rowGap: 1 }}>
            {chips}
          </Stack>
        )}
        <Typography variant="h4" sx={{ fontWeight: 800, wordBreak: 'break-word', overflowWrap: 'break-word' }}>
          {displayTitle || lesson.title}
        </Typography>
        <Typography color="text.secondary" sx={{ mt: 0.5 }}>{lesson.location}</Typography>
        <Alert severity="info" icon={false} sx={{ mt: 2, py: 0.9 }}>
          <Typography variant="caption" color="text.secondary">
            Work this page from top to bottom: understand the scenario, complete the concrete tasks, then record concise
            journal evidence. Practice is optional rehearsal only; your real evidence stays in your workbook, repo, or project file.
          </Typography>
        </Alert>
        <Stack
          direction="row"
          spacing={0.75}
          alignItems="center"
          sx={{ mt: 1.25, flexWrap: 'wrap', rowGap: 0.75 }}
        >
          {learnerFlow.map((label, index) => (
            <React.Fragment key={label}>
              <Chip
                size="small"
                label={label}
                variant={index === 0 ? 'filled' : 'outlined'}
                color={index === 0 ? 'primary' : 'default'}
                sx={{ fontWeight: 700 }}
              />
              {index < learnerFlow.length - 1 && (
                <Typography variant="caption" color="text.disabled" sx={{ fontWeight: 800 }}>
                  {'->'}
                </Typography>
              )}
            </React.Fragment>
          ))}
        </Stack>
      </Box>

      {/* Vertical timeline — numbered circles connected by a line communicate the sequence */}
      {steps.map((step, i) => {
        const isLastStep = i === steps.length - 1;
        const showConnector = !isLastStep || hasDeliverable;
        const isOpen = Boolean(openSteps[i]);
        return (
          <Box key={i} sx={{ display: 'flex', gap: 2.5, alignItems: 'flex-start' }}>
            {/* Left column: number circle + connector line to next step */}
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', flexShrink: 0 }}>
              <Box
                onClick={() => toggleStep(i)}
                sx={{
                  width: 32, height: 32, borderRadius: '50%',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  bgcolor: isOpen ? 'primary.main' : alpha(theme.palette.primary.main, 0.15),
                  color: isOpen ? 'primary.contrastText' : 'primary.main',
                  fontWeight: 800, fontSize: '0.88rem', zIndex: 1,
                  cursor: 'pointer', transition: 'background-color 0.2s',
                }}
              >
                {i + 1}
              </Box>
              {showConnector && (
                <Box sx={{ width: 2, flex: 1, minHeight: 32, bgcolor: 'divider', mt: 0.5 }} />
              )}
            </Box>

            {/* Right column: clickable header + collapsible content */}
            <Box sx={{ flex: 1, pb: showConnector ? 4 : 0, minWidth: 0 }}>
              <Box
                onClick={() => toggleStep(i)}
                sx={{
                  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                  cursor: 'pointer', userSelect: 'none', pt: 0.25, mb: isOpen ? 1.5 : 0,
                }}
              >
                <Typography variant="h6" sx={{ fontWeight: 700, lineHeight: 1.3, color: isOpen ? 'text.primary' : 'text.secondary' }}>
                  {step.label}
                </Typography>
                <Typography variant="caption" color="text.disabled" sx={{ ml: 1, flexShrink: 0, fontSize: '0.75rem' }}>
                  {isOpen ? '▲' : '▼'}
                </Typography>
              </Box>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: isOpen ? 1.25 : 0 }}>
                {stepDescriptions[step.key]}
              </Typography>
              <Collapse in={isOpen} timeout="auto" unmountOnExit>
                {step.content}
              </Collapse>
            </Box>
          </Box>
        );
      })}

      {/* Done marker — green check circle signals the finish line */}
      {hasDeliverable && (
        <Box sx={{ display: 'flex', gap: 2.5, alignItems: 'flex-start' }}>
          <Box sx={{
            width: 32, height: 32, borderRadius: '50%', flexShrink: 0,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            bgcolor: 'success.main', color: 'white', fontSize: '1rem', fontWeight: 800,
          }}>
            ✓
          </Box>
          <Box sx={{ flex: 1, pt: 0.25 }}>
            <Typography variant="h6" sx={{ fontWeight: 700, mb: 0.75 }}>You're done when…</Typography>
            <Typography color="text.secondary" sx={{ fontSize: '0.95rem', lineHeight: 1.7 }}>
              {lesson.deliverable}
            </Typography>
            <ReviewAndProofPanel lesson={lesson} />
          </Box>
        </Box>
      )}

      {/* Continue to next lesson CTA */}
      {onNextLesson && (
        <Box sx={{
          mt: 5, pt: 4,
          borderTop: '2px solid',
          borderColor: lessonReady ? 'success.main' : 'divider',
          display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1.5,
          transition: 'border-color 0.3s',
        }}>
          {!lessonReady && (
            <Stack direction="row" spacing={0.75} alignItems="center">
              <LockIcon sx={{ fontSize: '0.9rem', color: 'text.disabled' }} />
              <Typography variant="caption" color="text.disabled" sx={{ fontSize: '0.82rem' }}>
                {!allTasksDone
                  ? `Complete all tasks (${completedIndices.length}/${lesson.tasks.length} done) to continue`
                  : 'Rate your confidence in the journal step to continue'}
              </Typography>
            </Stack>
          )}
          {lessonReady && hasPracticeStep && !practiceComplete && (
            <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.82rem' }}>
              Practice is recommended for confidence, but it does not block the next lesson.
            </Typography>
          )}
          <Button
            variant="contained"
            size="large"
            disabled={!lessonReady}
            onClick={onNextLesson}
            endIcon={<ArrowForwardIcon />}
            sx={{
              px: 5, py: 1.5,
              fontSize: '1rem', fontWeight: 700,
              borderRadius: 2,
              bgcolor: lessonReady ? 'success.main' : undefined,
              '&:hover': lessonReady ? { bgcolor: 'success.dark' } : {},
              '&.Mui-disabled': { bgcolor: alpha(theme.palette.text.disabled, 0.12), color: 'text.disabled' },
              minWidth: 280,
              transition: 'background-color 0.3s',
            }}
          >
            {lessonReady
              ? (nextLessonTitle ? `Next lesson: ${nextLessonTitle}` : 'Continue to next lesson')
              : 'Continue to next lesson'}
          </Button>
        </Box>
      )}
    </Box>
  );
};

export default LessonView;
